import React, { useEffect, useState, useContext } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import Button from '../formfields/Button'
import { GlobalContext } from '../GlobalContext'
import FooterNavButtons from '../shared-components/FooterNavButtons'
import HeaderDetails from '../shared-components/HeaderDetails'
import RedAlert from '../ui-elements/RedAlert'
import Spinner from '../ui-elements/Spinner'
import { subscribe } from '../utils/pubsub'
import Modal from '../ui-elements/Modal'
import ChecklistSummary from './ChecklistSummary'
import OrgMismatchDialog from '../shared-components/OrgMismatchDialog'
import AssetPicker from './AssetPicker'
import JsonDebug from '../ui-elements/JsonDebug'
import { checkIfCheckPeriodIsLocked } from '../utils/checkIfCheckPeriodIsLocked'


type Props = {
    orgMismatchDialog: string | null
    setOrgMismatchDialog: React.Dispatch<React.SetStateAction<any>>
}


function StartChecklist({
    orgMismatchDialog,
    setOrgMismatchDialog
}: Props) {
    //console.log("***** StartChecklist.tsx")
    const {
        tableData,
        setShowModal,
        sendMessageToWebsocket,
        userData,
        loginOptions,
        currentOrganisation,
        fetchQuestionsAndAssetsForChecklistId,
        connectionState,
        loggedIn,
        externalUserState,
        t
    } = useContext(GlobalContext)

    const navigate = useNavigate()
    const { checklistId, assetId } = useParams()
    const [thisChecklist, setThisChecklist] = useState<ObjectStringKeyAnyValue | null>(null)
    const [checkPeriodsForChecklist, setCheckPeriodsForChecklist] = useState<ObjectStringKeyAnyValue | null>(null)
    const [activeCheckPeriodsForChecklist, setActiveCheckPeriodsForChecklist] = useState<ObjectStringKeyAnyValue | null>(null)
    const [showNotFound, setShowNotFound] = useState(false)
    const [thisChecklistQuestions, setThisChecklistQuestions] = useState<null | Array<ObjectStringKeyAnyValue>>(null)
    const [assetIdNeeded, setAssetIdNeeded] = useState(assetId ? false : true)
    const [adhocChecklistSubmitting, setAdhocChecklistSubmitting] = useState(false)
    const checklistFrequency = thisChecklist && thisChecklist['Schedule'] && thisChecklist['Schedule']['Frequency'] ? thisChecklist['Schedule']['Frequency'] : null
    const questionsForThisChecklist = thisChecklist && thisChecklistQuestions && thisChecklistQuestions.length > 0

    let titleText = thisChecklist && thisChecklist['ChecklistName'] ? thisChecklist['ChecklistName'] : 'Checklist'





    if (assetId) {
        let asset_name = ''
        if (tableData && tableData['Assets'] && tableData['Assets'][assetId]) {
            asset_name = tableData['Assets'][assetId]['AssetName']
        } else {
            console.log("asset name not found for asset id", assetId)
            //     const payload = JSON.stringify({
            //     action: "checklistsClientApp",
            //     subAction: "getAssetsByChecklistId",
            //     checklistId: checklistId,
            // })
            // sendMessageToWebsocket(payload)
        }
        titleText = `${titleText} for ${asset_name}`
    }

    useEffect(() => {
        if (checklistId) {
            fetchQuestionsAndAssetsForChecklistId(checklistId)
        }
    }, [thisChecklist])


    useEffect(() => {
        if (thisChecklist) {
            if (Object.keys(thisChecklist).indexOf('AssetTypeId') === -1) {
                setAssetIdNeeded(false)
            }
        }
    }, [thisChecklist])


    useEffect(() => {
        //console.log("StartChecklist.tsx useEffect")
        //console.log(assetId)

        if (checklistId && tableData && tableData.Checklists && userData) {

            if (tableData.Checklists[checklistId]) {
                setThisChecklist(tableData.Checklists[checklistId])
                setShowNotFound(false)
            } else {
                // console.log('⛔️ Checklist not found. Orgs for this user:')
                // console.log(userData.organisationsForUser)]
                // console.log(currentOrganisation)
                setShowNotFound(true)
            }

            if (tableData['ChecklistQuestions']) {
                let questionsArray: any[] = Object.values(tableData['ChecklistQuestions']).filter((question: any) => question.ChecklistId === checklistId)
                questionsArray.sort((a, b) => parseInt(a.QuestionOrder) - parseInt(b.QuestionOrder))
                setThisChecklistQuestions(questionsArray)
            }

            // filter check periods for this checklist
            const newCheckPeriodsForChecklist: ObjectStringKeyAnyValue = {}
            const newActiveCheckPeriodsForChecklist: ObjectStringKeyAnyValue = {}
            if (tableData && tableData.ChecklistPeriods) {
                for (const [key, value] of Object.entries(tableData.ChecklistPeriods)) {
                    if ((value as any).ChecklistId === checklistId) {
                        if (!assetId || (assetId == (value as any)['AssetId'])) {
                            newCheckPeriodsForChecklist[key] = value
                        }


                        // find other check periods the user can complete
                        const isCheckPeriodLocked = value && checkIfCheckPeriodIsLocked(value, thisChecklist, userData, loggedIn)

                        if ((value as any).InProgress === 'True' && !isCheckPeriodLocked) {

                            newActiveCheckPeriodsForChecklist[key] = value
                        }



                    }
                }
                setCheckPeriodsForChecklist(newCheckPeriodsForChecklist)
                setActiveCheckPeriodsForChecklist(newActiveCheckPeriodsForChecklist)

            }

        }

    }, [checklistId, tableData, userData, assetId])



    useEffect(() => {
        // if its for an external user, and we dont need to select a particular asset, just start the checklist
        if (loggedIn === 'externalUser' && !assetIdNeeded && checklistId) {
            startAdhocChecklistForExternalUser(checklistId)
        }
    }, [loggedIn, assetIdNeeded, checklistId])




    const startAdhocChecklist = () => {
        setShowModal({ "spinner": t('General.saving') })
        const payload = JSON.stringify({
            action: "checklistsClientApp",
            subAction: "startAdhocChecklist",
            checklistId: checklistId,
            assetId: assetId,
        })

        // when table data is updated with a check period for this checklist,
        // navigate to the checklist
        const unsubscribe = subscribe("tableUpdateItemAdded", data => {
            if (
                data.tableName === 'ChecklistPeriods' &&
                data.newRecord &&
                data.newRecord.Id &&
                data.newRecord.ChecklistId === checklistId
            ) {
                setShowModal(null)
                navigate(`/checklists/${data.newRecord.Id}`)
                unsubscribe()
            }
        })
        sendMessageToWebsocket(payload)
        setAdhocChecklistSubmitting(true)
    }

    const startAdhocChecklistForExternalUser = (checklistId: string) => {
        console.log('🤖 Starting adhoc checklist for external user')
        setShowModal({ "spinner": t('General.loading') })
        const payload = {
            action: "checklistsClientApp",
            subAction: "startAdhocChecklist",
            checklistId: checklistId
        }
        if (assetId) {
            (payload as any).assetId = assetId
        }
        sendMessageToWebsocket(JSON.stringify(payload))
    }






    return <div className={`w-full flex-1 h-full flex flex-col justify-between gap-2`}>


        {orgMismatchDialog && <OrgMismatchDialog
            orgMismatchDialog={orgMismatchDialog}
            setOrgMismatchDialog={setOrgMismatchDialog}
        />}




        <div className={`w-full flex flex-col items-center`}>
            <HeaderDetails
                titleText={titleText}
                subheadingText={assetId}
            />
            <div className={`max-w-3xl w-full p-5 flex flex-col gap-2 items-center justify-center text-center`}>

                {!tableData && <Spinner><p>{t('General.loading')}.</p></Spinner>}
                {showNotFound && <RedAlert>{t('Dashboard.StartChecklist.checklistNotFound', { checklistId })}</RedAlert>}

                {thisChecklist && checklistFrequency === 'WEEKLY' &&
                    <div className={`flex flex-col gap-4`}>
                        <p>{t('Dashboard.StartChecklist.currentCheckPeriods')}:</p>

                        {checkPeriodsForChecklist && Object.values(checkPeriodsForChecklist).length > 0 ?
                            Object.values(checkPeriodsForChecklist).map((checklistPeriod: any, index: number) => {
                                return <ChecklistSummary
                                    key={index}
                                    checklistId={checklistPeriod.ChecklistId}
                                    thisChecklist={thisChecklist}
                                    checkPeriodId={checklistPeriod.Id}
                                />
                            }) :

                            <RedAlert>{t('Dashboard.StartChecklist.noCheckPeriods')}</RedAlert>}

                    </div>}




                {thisChecklist && checklistFrequency === 'ADHOC' &&
                    <div className={`w-full flex flex-col gap-4`}>

                        {!assetIdNeeded && questionsForThisChecklist && <p>{t('Dashboard.StartChecklist.aboutToStartAdhoc')}</p>}

                        {!questionsForThisChecklist && <div className={`w-full flex flex-col gap-4`}>
                            {t('Dashboard.StartChecklist.noQuestions')}
                            <Button
                                text={t('Dashboard.StartChecklist.setUpQuestions')}
                                externalLinkUrl='https://dashboard.checqr.net/'
                            />
                        </div>}

                        {assetIdNeeded && <AssetPicker
                            assetTypeId={thisChecklist.AssetTypeId}
                            checklistId={thisChecklist.Id}
                        />}

                        {loggedIn !== 'externalUser' && !assetIdNeeded && questionsForThisChecklist && <Button
                            onClick={startAdhocChecklist}
                            text={t('Dashboard.StartChecklist.getStarted')}
                            size={'big'}
                            variant={questionsForThisChecklist && !adhocChecklistSubmitting ? `primary` : `gray`}
                            disabled={questionsForThisChecklist && !adhocChecklistSubmitting ? false : true}
                            id={`startAdhocChecklist`}
                        />}

                        {loggedIn !== 'externalUser' && activeCheckPeriodsForChecklist && Object.values(activeCheckPeriodsForChecklist).length > 0 && <div className={`w-full mt-4 flex flex-col gap-4`}>
                            <p>{t('Dashboard.StartChecklist.resumeCheckPeriod')}</p>

                            <div className={`w-full flex flex-col gap-2 text-left`}>
                                {Object.values(activeCheckPeriodsForChecklist).map((checklistPeriod: any, index: number) => {

                                    return <ChecklistSummary
                                        key={index}
                                        checklistId={checklistPeriod.ChecklistId}
                                        thisChecklist={thisChecklist}
                                        checkPeriodId={checklistPeriod.Id}
                                    />
                                })}</div>
                        </div>}



                    </div>}



            </div>
        </div>


    </div>
}



export default StartChecklist